import {
    getAllAttributeForSchema,
    getAttribute,
    getThingSchemaForId,
    sortAttributesFn,
    getOptionsForForAttribute,
    isRelationAttribute,
    findConditionInExperimentById,
} from "./thingUtils";
import { DateTime } from "luxon";
import _ from "lodash";
import {
    dateTimeObjToReadableDateString,
    dateTimeObjToReadableDateTimeString,
    getCurrentDateOnlyValue,
    getDateTimeForISODateOnlyString,
    getDaysBetweenDates,
    isDateTimeMidnight,
} from "./dateUtils";

export function isTaskStatusAvailable(dataCollectionTask) {
    return dataCollectionTask.type === "scheduled";
}

export function isTaskStatusSet(dataCollectionTask) {
    const status = dataCollectionTask.fieldStatus;
    return !_.isNil(status);
}

export function isTaskStatusDone(dataCollectionTask) {
    return dataCollectionTask.fieldStatus === "DONE";
}

export function buildTaskStatusDescription(dataCollectionTask, siteTimeZone) {
    if (!isTaskStatusSet(dataCollectionTask)) {
        return "";
    }

    const status = dataCollectionTask.fieldStatus;
    const timestampDateTime = DateTime.fromISO(dataCollectionTask.fieldStatusUpdatedAt, {
        zone: siteTimeZone,
    });
    const timestampString = timestampDateTime.toLocaleString(DateTime.DATETIME_MED);
    return `${status} by ${dataCollectionTask.fieldStatusSetByUserId} on ${timestampString}`;
}

export function findTaskInTaskSchedule(taskId, taskSchedule) {
    if (
        taskSchedule.defaultDataCollectionTask &&
        taskId === taskSchedule.defaultDataCollectionTask.id
    ) {
        return taskSchedule.defaultDataCollectionTask;
    }

    return findTaskById(taskId, taskSchedule.dataCollectionTasks);
}

export function findTaskById(taskId, tasks) {
    return tasks.find((task) => task.id === taskId);
}

export function getTaskDayDescription({
    day,
    date,
    isRelativeDay0,
    isInheritedFromTaskGroup,
    selectedTaskGroupId,
    taskGroups,
}) {
    const taskGroup = selectedTaskGroupId
        ? taskGroups.find((taskGroup) => taskGroup.id === selectedTaskGroupId)
        : null;

    if (!taskGroup) {
        return "";
    }

    function getTaskGroupDateString() {
        return getDateTimeForISODateOnlyString(taskGroup.calculatedDate).toISODate();
    }

    if (isInheritedFromTaskGroup) {
        if (_.isNil(taskGroup.calculatedDate)) {
            return `Day ${taskGroup.day} inherited from the Task Group ${taskGroup.displayName}`;
        } else {
            const isoDateString = getTaskGroupDateString();
            return `Date ${isoDateString} inherited from the Task Group ${taskGroup.displayName}`;
        }
    } else {
        if (isRelativeDay0) {
            if (_.isNil(taskGroup.day)) {
                // this means we're override the task group, but the task group is not relative to day 0
                const isoDateString = getTaskGroupDateString();
                return `Day ${day} overrides Task Group ${taskGroup.displayName} date ${isoDateString}`;
            } else {
                return `Day ${day} overrides Task Group ${taskGroup.displayName} day ${taskGroup.day}`;
            }
        } else {
            if (_.isNil(taskGroup.day)) {
                const isoDateString = getTaskGroupDateString();
                return `Date ${date} overrides Task Group ${taskGroup.displayName} date ${isoDateString}`;
            } else {
                return `Date ${date} overrides Task Group ${taskGroup.displayName} day ${taskGroup.day}`;
            }
        }
    }
}

export function getTaskDisplayNameInTaskSchedule(
    dataCollectionTask,
    taskSchedule,
    experimentStartDate
) {
    if (
        taskSchedule.defaultDataCollectionTask &&
        dataCollectionTask.id === taskSchedule.defaultDataCollectionTask.id
    ) {
        return "Default Task";
    }
    if (dataCollectionTask.type === "ad-hoc") {
        return dataCollectionTask.name;
    }

    const taskGroup = findTaskGroupById(dataCollectionTask.taskGroupId, taskSchedule.taskGroups);
    const readoutGroup = findReadoutGroupById(
        dataCollectionTask.readoutGroupId,
        taskSchedule.readoutGroups
    );
    return getTaskDisplayName(dataCollectionTask, taskGroup, readoutGroup, experimentStartDate);
}

export function getTaskDisplayName(
    dataCollectionTask,
    taskGroup,
    readoutGroup,
    experimentStartDate
) {
    if (dataCollectionTask.type === "ad-hoc") {
        return dataCollectionTask.name;
    }

    const taskDate = getDateForTask(dataCollectionTask, taskGroup, experimentStartDate);
    if (readoutGroup) {
        return `${taskGroup.displayName} on ${taskDate} for ${readoutGroup.name}`;
    } else {
        return `${taskGroup.displayName} on ${taskDate}`;
    }
}

export function getDefaultAnchorDateValue(date) {
    if (date) {
        return getDateTimeForISODateOnlyString(date).toISODate();
    } else {
        return getCurrentDateOnlyValue().toISODate();
    }
}

export function getDayForTaskGroup(taskGroup, experimentStartDate) {
    const dateTime = getDateTimeForISODateOnlyString(taskGroup.calculatedDate);
    return getDaysBetweenDates(experimentStartDate, dateTime);
}

export function getDateForTaskGroup(taskGroup) {
    return getDateTimeForISODateOnlyString(taskGroup.calculatedDate).toISODate();
}

export function getAdHocTaskDate(adHocDataCollectionTask, siteTimeZone) {
    if (_.isNil(adHocDataCollectionTask.date)) {
        return ""; // Note: should always have a date, but not enforced yet
    }

    const dateTimeISOString = adHocDataCollectionTask.date;
    const dateTime = DateTime.fromISO(dateTimeISOString, { zone: siteTimeZone });
    if (isDateTimeMidnight(dateTime)) {
        return dateTimeObjToReadableDateString(dateTime, siteTimeZone);
    } else {
        return dateTimeObjToReadableDateTimeString(dateTime, siteTimeZone);
    }
}

export function getDateForTaskInTaskGroups(dataCollectionTask, taskGroups, experimentStartDate) {
    if (experimentStartDate === null || experimentStartDate === undefined) {
        return "";
    }

    const day = findTaskDayInTaskGroups(dataCollectionTask, taskGroups, experimentStartDate);
    return getDateFromExperimentStartDateForDay(experimentStartDate, day);
}

export function getDateFromExperimentStartDateForDay(experimentStartDate, day) {
    return experimentStartDate.plus({ days: day }).toISODate();
}

/**
 * Return the luxon DateTime for the given task
 * @param dataCollectionTask
 * @param taskGroup
 * @param experimentStartDate
 * @param siteTimeZone
 */
export function getDateForTask(dataCollectionTask, taskGroup, experimentStartDate, siteTimeZone) {
    if (experimentStartDate === null || experimentStartDate === undefined) {
        // Note: this shouldn't happen; the experiment start date should always be set unless it's an old experiment
        return "";
    }

    if (!_.isNil(dataCollectionTask.date)) {
        return getDateTimeForISODateOnlyString(dataCollectionTask.date).toISODate();
    }

    const taskDay = dataCollectionTask.day;
    if (!_.isNil(taskDay)) {
        return dayToDate(experimentStartDate, taskDay);
    }

    return getDateForTaskGroup(taskGroup);
}

export function getDayForTask(dataCollectionTask, taskGroup, experimentStartDate) {
    const taskDate = getDateForTask(dataCollectionTask, taskGroup, experimentStartDate);
    if (taskDate) {
        const dateTime = getDateTimeForISODateOnlyString(taskDate);
        return getDaysBetweenDates(experimentStartDate, dateTime);
    } else {
        return null;
    }
}

export function dayToDate(experimentStartDate, day) {
    return experimentStartDate.plus({ days: day }).toISODate();
}

export function findTaskDayInTaskGroups(task, taskGroups, experimentStartDate) {
    const taskDay = task.day;
    if (!_.isNil(taskDay)) {
        return taskDay;
    }

    const taskDate = task.date;
    if (!_.isNil(taskDate)) {
        const dateTime = getDateTimeForISODateOnlyString(taskDate);
        return getDaysBetweenDates(experimentStartDate, dateTime);
    }

    const taskGroup = findTaskGroupById(task.taskGroupId, taskGroups);
    return getDayForTaskGroup(taskGroup, experimentStartDate);
}

export function findReadoutGroupsForIds(readoutGroupIds, readoutGroups) {
    if (!readoutGroupIds) {
        return [];
    }
    return readoutGroupIds
        .map((readoutGroupId) => {
            return findReadoutGroupById(readoutGroupId, readoutGroups);
        })
        .filter((g) => g);
}

export function findTaskGroupById(taskGroupId, taskGroups) {
    return taskGroups.find((taskGroup) => taskGroup.id === taskGroupId);
}

export function findReadoutGroupById(readoutGroupId, readoutGroups) {
    return readoutGroups.find((readoutGroup) => readoutGroup.id === readoutGroupId);
}

function getReadoutGroupForTask(dataCollectionTask, readoutGroups) {
    return findReadoutGroupById(dataCollectionTask.readoutGroupId, readoutGroups);
}

export function isAttributeNameInReadoutGroup(readoutGroup, thingSchema, attributeName) {
    const attribute = getAttribute(thingSchema, attributeName);
    return isAttributeInReadoutGroup(readoutGroup, attribute);
}

export function isAttributeInReadoutGroup(readoutGroup, attribute) {
    return (
        readoutGroup.requestedAttributes.find((requestedAttribute) => {
            return (
                requestedAttribute.attributeName === attribute.name ||
                // subordinate attributes are included if the associated attribute is included
                requestedAttribute.attributeName === attribute.subordinate_to_attribute
            );
        }) !== undefined
    );
}

export function getReadoutGroupAttributes(readoutGroup, thingSchema) {
    const allSchemaAttributes = getAllAttributeForSchema(thingSchema);

    const readoutGroupAttributes = allSchemaAttributes.filter((attribute) => {
        return isAttributeInReadoutGroup(readoutGroup, attribute);
    });

    readoutGroupAttributes.sort(sortAttributesFn);

    return readoutGroupAttributes;
}

/**
 * Get the attributes for a readout group in the given ThingSchema
 */
export function getAttributesForReadoutGroup(readoutGroup, thingSchema) {
    const allSchemaAttributes = getAllAttributeForSchema(thingSchema);
    const readoutGroupAttributes = allSchemaAttributes.filter((attribute) => {
        return isAttributeInReadoutGroup(readoutGroup, attribute);
    });

    readoutGroupAttributes.sort(sortAttributesFn);

    return readoutGroupAttributes;
}

/**
 * Stateful helper for working with a task schedule's set of objects
 */
export function buildTaskScheduleHelper({ readoutGroups, taskGroups }) {
    return {
        getTaskGroupById(taskGroupId) {
            return taskGroups.find((taskGroup) => taskGroup.id === taskGroupId);
        },
    };
}

export function getTaskReferenceById(taskReferences, taskReferenceId) {
    return taskReferences.find((taskReference) => taskReference.id === taskReferenceId);
}

export function getTaskReferenceDisplay(
    taskGroupFromReference,
    taskReference,
    protocol,
    experimentStartDate
) {
    const day = taskGroupFromReference.dayAfterReference;
    if (taskReference.referenceExperimentStartDate) {
        return `${day} days after experiment start date ${experimentStartDate.toFormat("yyyy-MM-dd")}`;
    } else {
        const thingSchemaId = taskReference.thingSchemaId;
        const referenceAttributeName = taskReference.referenceAttributeName;
        const thingSchema = getThingSchemaForId(protocol, thingSchemaId);
        const attribute = getAttribute(thingSchema, referenceAttributeName);
        const thingId = taskGroupFromReference.referencedThingId;
        // TODO: time zone
        const referencedThingDateTime = DateTime.fromISO(
            taskGroupFromReference.referencedThingDateTime
        ).toFormat("yyyy-MM-dd");
        return `${day} days after ${thingSchema.displayName} ${thingId} ${attribute.display_name} of ${referencedThingDateTime}`;
    }
}

export function buildReadoutGroupOptions(readoutGroups) {
    return readoutGroups.map((readoutGroup) => {
        let description = "";
        if (readoutGroup.description) {
            description = " - " + readoutGroup.description;
        }
        return {
            title: `${readoutGroup.name + description}`,
            value: readoutGroup.id,
        };
    });
}

export function buildTaskReferenceOptions(taskReferences) {
    return taskReferences.map((taskReference) => {
        return {
            title: taskReference.name,
            value: taskReference.id,
        };
    });
}

export function buildTaskThingDateEstimateOptions(taskThingDateEstimates) {
    return taskThingDateEstimates.map((taskThingDateEstimate) => {
        return {
            title: `${taskThingDateEstimate.thingId} on day ${taskThingDateEstimate.day}`,
            value: taskThingDateEstimate.id,
        };
    });
}

function isThingFilterPresent(dataCollectionTask) {
    return dataCollectionTask.filter && dataCollectionTask.filter.thing;
}

function isTaskFilteredByCondition(dataCollectionTask) {
    return _.isNil(dataCollectionTask.filter.thing.conditionId);
}

function isTaskFilteredByAttribute(dataCollectionTask) {
    const attributesFilter = dataCollectionTask.filter.thing.attributes;
    return !_.isNil(attributesFilter);
}

export function isTaskFilterConfigured(dataCollectionTask) {
    return (
        isThingFilterPresent(dataCollectionTask) &&
        (isTaskFilteredByCondition(dataCollectionTask) ||
            isTaskFilteredByAttribute(dataCollectionTask))
    );
}

export function buildTaskFilterDisplay(dataCollectionTask, readoutGroup, experiment, protocol) {
    if (!isTaskFilterConfigured(dataCollectionTask)) {
        return "";
    }

    const filterDescriptions = [];
    const attributeFilterDescription = buildAttributeFilterDescription(
        dataCollectionTask,
        readoutGroup,
        experiment,
        protocol
    );

    if (attributeFilterDescription) {
        filterDescriptions.push(attributeFilterDescription);
    }

    const conditionFilterDescription = buildConditionFilterDescription(
        dataCollectionTask,
        experiment
    );

    if (conditionFilterDescription) {
        filterDescriptions.push(conditionFilterDescription);
    }

    return filterDescriptions.join(", ");
}

export function buildConditionFilterDescription(dataCollectionTask, experiment) {
    const conditionIdValue = dataCollectionTask.filter.thing.conditionId;
    if (!conditionIdValue || conditionIdValue.length === 0) {
        return null;
    }

    const conditionIds = _.isString(conditionIdValue) ? [conditionIdValue] : conditionIdValue;

    const selectedConditions = conditionIds.map((conditionId) => {
        const condition = findConditionInExperimentById(experiment, conditionId);
        if (condition) {
            return `Condition: ${condition.number} ${condition.name}`;
        } else {
            return `Unknown condition ${conditionId}`;
        }
    });
    return selectedConditions.join(", ");
}

export function buildAttributeFilterDescription(
    dataCollectionTask,
    readoutGroup,
    experiment,
    protocol
) {
    const thingSchema = getThingSchemaForId(protocol, readoutGroup.thingSchemaId);

    const attributeFilters = dataCollectionTask.filter.thing.attributes;

    const descriptions = attributeFilters.map((attributeFilter) => {
        const attribute = getAttribute(thingSchema, attributeFilter.name);
        if (isRelationAttribute(attribute)) {
            const options = getOptionsForForAttribute(attribute, experiment, protocol);
            const value = attributeFilter.value;
            const displayValues = value.map((value) => {
                const option = options.find((option) => option.value === value);
                // we have some malformed filters that have the display name stored instead of the id;
                // checking truthiness of attribute is so that we don't blow up on those
                return option ? option.displayName : value;
            });
            return `${attributeFilter.name}: ${displayValues.join(", ")}`;
        } else {
            return `${attributeFilter.name}: ${attributeFilter.value}`;
        }
    });

    if (descriptions) {
        return descriptions.join(", ");
    } else {
        return null;
    }
}

export function buildSelectRelativeToDay0Field(opts) {
    return {
        ...opts,
        fieldType: "boolean",
        fieldDisplayName: `Specify day relative to Day 0?`,
        cols: 12,
        appendSlot: "dayFieldDescription",
    };
}

export function getDefaultAnchorDateValueForTask({
    dataCollectionTask,
    taskGroups,
    overrideTaskGroupDay,
    experimentStartDate,
}) {
    if (overrideTaskGroupDay) {
        const date = getDateForTaskInTaskGroups(
            dataCollectionTask,
            taskGroups,
            experimentStartDate
        );
        return getDefaultAnchorDateValue(date);
    } else {
        if (dataCollectionTask.taskGroupId) {
            const taskGroup = findTaskGroupById(dataCollectionTask.taskGroupId, taskGroups);
            return getDefaultAnchorDateValue(taskGroup.date);
        } else {
            // if this is a new task, there's no TaskGroup to defer to.
            return getDefaultAnchorDateValue(null);
        }
    }
}

export function validateRelativeToTaskEstimateTaskGroup(taskGroup) {
    if (_.isNil(taskGroup.taskThingDateEstimateId) || "" === taskGroup.taskThingDateEstimateId) {
        return "Please select a task thing date estimate";
    }
    if (_.isNil(taskGroup.dayAfterReference) || "" === taskGroup.dayAfterReference) {
        return "Please enter a day after reference";
    }

    return null;
}
